import { LitElement, css, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { styleMap } from "lit/directives/style-map.js";
import { marked } from "marked";
import type { AgentMessage } from "../types";

interface ResizeState {
  handle: 'top' | 'left' | 'corner';
  startX: number;
  startY: number;
  startWidth: number;
  startHeight: number;
}

@customElement("chatbot-widget")
export class ChatbotWidget extends LitElement {
  @property({ type: Number, reflect: true, attribute: 'integration-id' }) integrationId = 0;
  @property({ type: String, reflect: true, attribute: 'api-key' }) apiKeyAttr = "";
  @property({ type: String, reflect: true }) conversationId = "";
  @state() private apiKey = "";
  @state() private apiUrl = "";
  @state() botData: any = null;
  @state() private sessionId: string = "";
  @state() loading = false;
  @state() error = "";
  @state() isOpen = false;
  @state() messages: Array<{
    type: "user" | "bot" | "agent";
    content: string;
    timestamp: Date;
    id?: number;
    feedback?: "up" | "down";
    feedbackSubmitted?: boolean;
    agent_id?: number;
    image_url?: string;
    audio_url?: string;
    audio_duration?: number;
  }> = [];
  @state() currentMessage = "";
  /**
   * Human-handoff-mvp: public reactive state. Set to true when the SSE
   * publisher emits `handoff_active`, cleared by `handoff_closed`. The
   * banner is rendered only when this is true. Public (no underscore) per
   * design §8.2 so callers can inspect the handoff state if they need to.
   */
  @state() handoffActive = false;
  @state() sendingMessage = false;
  @state() currentConversationId: string | null = null;
  @state() conversationTitle: string | null = null;
  @state() loadingConversationHistory = false;
  @state() private initialized = false;
  @state() private isStreaming = false;
  @state() private streamingContent = "";
  @state() private thinkingMessage = "";
  @state() private _containerWidth = 350;
  @state() private _containerHeight = 500;
  @state() private recordingState: "idle" | "recording" | "sending" = "idle";
  @state() private recordingDuration = 0;
  @state() private lightboxUrl: string | null = null;
  @state() private pendingAttachment: { file: File; previewUrl: string } | null = null;
  @state() private isDraggingFile = false;
  private _dragDepth = 0;
  private _mediaRecorder: MediaRecorder | null = null;
  private _audioChunks: Blob[] = [];
  private _recordingTimer: number | null = null;
  private _audioStream: MediaStream | null = null;
  private _streamController: AbortController | null = null;
  private _resizeState: ResizeState | null = null;
  private _rafId: number | null = null;

  private formatMessage(content: string): string {
    try {
      marked.setOptions({
        breaks: true,
        gfm: true,
      });

      const html = marked.parse(content);
      return typeof html === "string" ? html : content;
    } catch (error) {
      console.error("Error formatting markdown:", error);
      return content;
    }
  }

  private getStorageKey(): string {
    // Use apiKey if available, else integrationId (backwards compatibility)
    const keyId = this.apiKeyAttr || this.integrationId?.toString() || '';
    return `chatbot_conversation_${keyId}`;
  }

  private initSession(): void {
    // Use apiKey if available, else integrationId (backwards compatibility)
    const keyId = this.apiKeyAttr || this.integrationId?.toString() || '';
    const storageKey = `chatbot_session_${keyId}`;
    let savedSessionId = localStorage.getItem(storageKey);

    if (!savedSessionId) {
      savedSessionId = crypto.randomUUID();
      localStorage.setItem(storageKey, savedSessionId);
      console.log("ChatbotWidget: Created new session ID:", savedSessionId);
    } else {
      console.log("ChatbotWidget: Loaded session ID from localStorage:", savedSessionId);
    }

    this.sessionId = savedSessionId;
  }

  private saveConversationId(conversationId: string): void {
    if (conversationId && this.integrationId) {
      localStorage.setItem(this.getStorageKey(), conversationId);
      console.log(
        "ChatbotWidget: Saved conversation ID to localStorage:",
        conversationId
      );
    }
  }

  private loadConversationId(): string | null {
    if (!this.integrationId) return null;
    const conversationId = localStorage.getItem(this.getStorageKey());
    if (conversationId) {
      console.log(
        "ChatbotWidget: Loaded conversation ID from localStorage:",
        conversationId
      );
    }
    return conversationId;
  }

  private clearConversationId(): void {
    if (this.integrationId) {
      localStorage.removeItem(this.getStorageKey());
      console.log("ChatbotWidget: Cleared conversation ID from localStorage");
    }
  }

  private scrollToBottom() {
    this.updateComplete.then(() => {
      requestAnimationFrame(() => {
        const messagesContainer = this.shadowRoot?.querySelector(
          ".messages-container"
        ) as HTMLElement;
        if (messagesContainer) {
          messagesContainer.scrollTop = messagesContainer.scrollHeight;
        }
      });
    });
  }

  private scrollToLatestElement() {
    this.updateComplete.then(() => {
      // Add a small delay to ensure DOM is fully updated
      setTimeout(() => {
        requestAnimationFrame(() => {
          const messagesContainer = this.shadowRoot?.querySelector(
            ".messages-container"
          ) as HTMLElement;
          if (messagesContainer) {
            const messages = messagesContainer.querySelectorAll(
              ".message, .typing-indicator"
            );
            if (messages.length > 0) {
              const latestElement = messages[
                messages.length - 1
              ] as HTMLElement;
              if (latestElement) {
                const elementTop = latestElement.offsetTop;

                // Scroll to show the element at the top of the visible area
                messagesContainer.scrollTop = elementTop;
              }
            }
          }
        });
      }, 50);
    });
  }

  private updateBotColors() {
    if (this.botData?.chat_color) {
      const botColor = this.botData.chat_color;
      const rgb = this.hexToRgb(botColor);
      const hoverColor = this.adjustColor(botColor, -20);

      this.style.setProperty("--bot-color", botColor);
      this.style.setProperty("--bot-color-hover", hoverColor);
      this.style.setProperty("--bot-color-light", `${botColor}1a`);
      this.style.setProperty("--bot-color-shadow", `${botColor}66`);
      if (rgb) {
        this.style.setProperty(
          "--bot-color-rgb",
          `${rgb.r}, ${rgb.g}, ${rgb.b}`
        );
      }

      console.log("ChatbotWidget: Updated bot colors:", {
        color: botColor,
        hover: hoverColor,
        rgb: rgb,
      });
    } else {
      console.log("ChatbotWidget: No bot color found, using defaults");
      this.style.setProperty("--bot-color", "#4287f5");
      this.style.setProperty("--bot-color-hover", "#3367d6");
      this.style.setProperty("--bot-color-light", "rgba(66, 135, 245, 0.1)");
      this.style.setProperty("--bot-color-shadow", "rgba(66, 135, 245, 0.4)");
      this.style.setProperty("--bot-color-rgb", "66, 135, 245");
    }
  }

  private hexToRgb(hex: string): { r: number; g: number; b: number } | null {
    let cleanHex = hex.replace("#", "");

    if (cleanHex.length === 3) {
      cleanHex = cleanHex
        .split("")
        .map((char) => char + char)
        .join("");
    }

    const result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(cleanHex);
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : null;
  }

  private adjustColor(hex: string, amount: number): string {
    const rgb = this.hexToRgb(hex);
    if (!rgb) return hex;

    const r = Math.max(0, Math.min(255, rgb.r + amount));
    const g = Math.max(0, Math.min(255, rgb.g + amount));
    const b = Math.max(0, Math.min(255, rgb.b + amount));

    const toHex = (n: number) => n.toString(16).padStart(2, "0");
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  connectedCallback() {
    super.connectedCallback();
    this.initSession();
    this.initializeConversation();
    document.addEventListener('mousemove', this._onResizeMove);
    document.addEventListener('mouseup', this._onResizeEnd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._streamController) {
      this._streamController.abort();
      this._streamController = null;
    }
    document.removeEventListener('mousemove', this._onResizeMove);
    document.removeEventListener('mouseup', this._onResizeEnd);
    if (this._rafId !== null) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
  }

  private async initializeConversation() {
    // Check for apiKeyAttr or integrationId (T-010 priority logic)
    const keyId = this.apiKeyAttr || this.integrationId;
    if (keyId && !this.initialized) {
      this.initialized = true;
      const savedConversationId = this.loadConversationId();
      if (savedConversationId) {
        console.log(
          "ChatbotWidget: Found saved conversation ID:",
          savedConversationId
        );
        this.currentConversationId = savedConversationId;
        await this.loadConversationHistory();
      } else {
        console.log("ChatbotWidget: No saved conversation ID found");
      }
    }
  }

  static styles = css`
    :host {
      position: fixed;
      bottom: 20px;
      right: 20px;
      z-index: 9999;
      font-family: Arial, sans-serif;
      --bot-color: #4287f5;
      --bot-color-hover: #3367d6;
      --bot-color-light: rgba(66, 135, 245, 0.1);
      --bot-color-shadow: rgba(66, 135, 245, 0.4);
    }

    .floating-button {
      width: 60px;
      height: 60px;
      border-radius: 50%;
      background: var(--bot-color);
      border: none;
      color: white;
      font-size: 24px;
      cursor: pointer;
      box-shadow: 0 4px 12px var(--bot-color-shadow);
      transition: all 0.3s ease;
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
      z-index: 10000;
      animation: pulse 2s infinite;
    }

    @keyframes pulse {
      0% {
        box-shadow: 0 4px 12px var(--bot-color-shadow);
      }
      50% {
        box-shadow: 0 4px 20px rgba(var(--bot-color-rgb, 66, 135, 245), 0.6),
          0 0 0 8px rgba(var(--bot-color-rgb, 66, 135, 245), 0.1);
      }
      100% {
        box-shadow: 0 4px 12px var(--bot-color-shadow);
      }
    }

    .floating-button:hover {
      transform: scale(1.1);
      box-shadow: 0 6px 16px rgba(var(--bot-color-rgb, 66, 135, 245), 0.6);
      animation: none;
    }

    .resize-wrapper {
      position: absolute;
      bottom: 80px;
      right: 0;
      z-index: 9999;
    }

    .chat-container {
      width: 100%;
      height: 100%;
      background: #fff;
      border-radius: 12px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
      border: 1px solid #e0e0e0;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      opacity: 0;
      transform: scale(0.3) translateY(20px);
      transform-origin: bottom right;
      transition: opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1),
                  transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      pointer-events: none;
      z-index: 10001;
    }

    .chat-container.open {
      opacity: 1;
      transform: scale(1) translateY(0);
      pointer-events: all;
    }

    .chat-header {
      padding: 16px;
      font-size: 1em;
      font-weight: normal;
      display: flex;
      align-items: center;
      justify-content: space-between;
      border-bottom: 1px solid #e0e0e0;
      min-height: 50px;
      background-color: white;
      color: #333;
      border-top-left-radius: 12px;
      border-top-right-radius: 12px;
    }

    .bot-info {
      display: flex;
      align-items: center;
      gap: 12px;
      flex: 1;
    }

    .bot-avatar {
      width: 44px;
      height: 44px;
      border-radius: 50%;
      object-fit: cover;
      border: none;
      background-color: transparent;
    }

    .bot-name {
      font-size: 1.3em;
      font-weight: 400;
      color: #555;
    }

    .header-actions {
      display: flex;
      gap: 8px;
      align-items: center;
    }

    .new-conversation-button {
      background: var(--bot-color-light);
      border: none;
      color: var(--bot-color);
      cursor: pointer;
      padding: 8px;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      transition: all 0.2s ease;
    }

    .new-conversation-button:hover {
      background: rgba(var(--bot-color-rgb, 66, 135, 245), 0.2);
      color: var(--bot-color-hover);
      transform: scale(1.1);
    }

    .new-conversation-icon {
      width: 18px;
      height: 18px;
      fill: currentColor;
    }

    .close-button {
      background: none;
      border: none;
      color: #666;
      font-size: 18px;
      cursor: pointer;
      padding: 0;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      transition: background-color 0.2s ease;
    }

    .close-button:hover {
      background: rgba(0, 0, 0, 0.1);
    }

    .chat-body {
      flex: 1;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      position: relative;
    }
    .drop-zone-overlay {
      position: absolute;
      inset: 0;
      background: rgba(79, 70, 229, 0.18);
      border: 2px dashed var(--bot-color, #4f46e5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10;
      pointer-events: none;
      animation: dropzone-fade 120ms ease-out;
    }
    .drop-zone-card {
      background: var(--widget-bg, #ffffff);
      color: var(--bot-color, #4f46e5);
      border-radius: 12px;
      padding: 20px 28px;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 6px;
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
    }
    .drop-zone-title {
      font-size: 14px;
      font-weight: 600;
    }
    .drop-zone-subtitle {
      font-size: 12px;
      color: #6b7280;
    }
    @keyframes dropzone-fade {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    .messages-container {
      flex: 1;
      overflow-y: auto;
      padding: 16px;
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .message {
      max-width: 80%;
      padding: 12px 16px;
      border-radius: 18px;
      word-wrap: break-word;
      line-height: 1.4;
      font-size: 15px;
    }

    .message.user {
      align-self: flex-end;
      background: var(--bot-color);
      color: white;
      border-bottom-right-radius: 4px;
    }

    .message.bot {
      align-self: flex-start;
      background: #f1f3f4;
      color: #333;
      border-bottom-left-radius: 4px;
      text-align: left;
      position: relative;
      padding-bottom: 28px;
    }

    /* Phase 1 multimodal ingest: outlined variant for image/audio bubbles.
       No fill — only a border in the bot accent color and a subtle tinted
       background so the bubble is recognizable but doesn't compete
       visually with normal text messages. */
    .message.has-image,
    .message.has-audio {
      background: rgba(79, 70, 229, 0.06);
      border: 1px solid var(--bot-color, #4f46e5);
      color: inherit;
      padding: 8px;
    }
    .message.user.has-image,
    .message.user.has-audio {
      background: transparent;
      color: #1f2937;
      border-bottom-right-radius: 18px;
    }
    .message.bot.has-image,
    .message.bot.has-audio {
      padding-bottom: 8px;
    }

    /* Human-handoff-mvp: agent variant. Same side as a bot bubble but
       distinctly tinted so the user can tell a human answered instead of
       the LLM. */
    .message.agent {
      align-self: flex-start;
      background: #e8f1ff;
      color: #1a3a6e;
      border-bottom-left-radius: 4px;
      text-align: left;
      position: relative;
      padding-bottom: 14px;
      border-left: 3px solid var(--bot-color, #1f7aec);
    }

    .agent-badge {
      display: inline-block;
      margin-bottom: 6px;
      padding: 2px 8px;
      font-size: 11px;
      font-weight: 600;
      letter-spacing: 0.4px;
      text-transform: uppercase;
      color: #ffffff;
      background: var(--bot-color, #1f7aec);
      border-radius: 10px;
    }

    .message.bot h1,
    .message.bot h2,
    .message.bot h3,
    .message.bot h4,
    .message.bot h5,
    .message.bot h6 {
      margin: 0.5em 0 0.3em 0;
      font-weight: 600;
      line-height: 1.2;
      text-align: left;
    }

    .message.bot h1 {
      font-size: 1.3em;
    }
    .message.bot h2 {
      font-size: 1.2em;
    }
    .message.bot h3 {
      font-size: 1.1em;
    }
    .message.bot h4 {
      font-size: 1.05em;
    }
    .message.bot h5 {
      font-size: 1.02em;
    }
    .message.bot h6 {
      font-size: 1em;
    }

    .message.bot p {
      margin: 0.5em 0;
      line-height: 1.4;
      text-align: left;
    }

    .message.bot ul,
    .message.bot ol {
      margin: 0.5em 0;
      padding-left: 1.5em;
      text-align: left;
    }

    .message.bot li {
      margin: 0.2em 0;
      line-height: 1.4;
      text-align: left;
    }

    .message.bot strong,
    .message.bot b {
      font-weight: 600;
    }

    .message.bot em,
    .message.bot i {
      font-style: italic;
    }

    .message.bot code {
      background: rgba(0, 0, 0, 0.1);
      padding: 0.2em 0.4em;
      border-radius: 3px;
      font-family: "Courier New", monospace;
      font-size: 0.93em;
    }

    .message.bot pre {
      background: rgba(0, 0, 0, 0.1);
      padding: 0.8em;
      border-radius: 6px;
      overflow-x: auto;
      margin: 0.5em 0;
      text-align: left;
    }

    .message.bot pre code {
      background: none;
      padding: 0;
      text-align: left;
    }

    .message.bot blockquote {
      border-left: 3px solid #ccc;
      margin: 0.5em 0;
      padding-left: 1em;
      color: #666;
      text-align: left;
    }

    .message.bot a {
      color: var(--bot-color);
      text-decoration: underline;
    }

    .message.bot a:hover {
      color: var(--bot-color-hover);
    }

    .message.bot table {
      border-collapse: collapse;
      width: 100%;
      margin: 0.5em 0;
    }

    .message.bot th,
    .message.bot td {
      border: 1px solid #ddd;
      padding: 0.5em;
      text-align: left;
    }

    .message.bot th {
      background: rgba(0, 0, 0, 0.1);
      font-weight: 600;
    }

    .message-input-container {
      padding: 16px;
      border-top: 1px solid #e0e0e0;
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
    .message-input-row {
      display: flex;
      gap: 8px;
      align-items: center;
    }

    .message-input {
      flex: 1;
      border: 1px solid #ddd;
      border-radius: 20px;
      padding: 10px 16px;
      font-size: 14px;
      outline: none;
      resize: none;
      max-height: 100px;
      min-height: 20px;
    }

    .message-input:focus {
      border-color: var(--bot-color);
    }

    .send-button {
      background: var(--bot-color);
      color: white;
      border: none;
      border-radius: 50%;
      width: 36px;
      height: 36px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background-color 0.2s ease;
    }

    .send-button:hover:not(:disabled) {
      background: var(--bot-color-hover);
    }

    .send-button:disabled {
      background: #ccc;
      cursor: not-allowed;
    }

    .mic-button {
      background: var(--bot-color, #4f46e5);
      color: white;
      border: none;
      border-radius: 50%;
      width: 36px;
      height: 36px;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      transition: background-color 0.2s ease;
      flex-shrink: 0;
      padding: 0;
      gap: 4px;
    }
    .mic-button:hover:not(:disabled) {
      opacity: 0.85;
    }
    .mic-button:disabled {
      background: #9ca3af;
      cursor: not-allowed;
    }
    .mic-button.recording {
      background: #e53935;
      width: auto;
      border-radius: 36px;
      padding: 0 12px;
      gap: 6px;
      animation: mic-pulse 1.5s ease-in-out infinite;
    }
    @keyframes mic-pulse {
      0%, 100% { box-shadow: 0 0 0 0 rgba(229, 57, 53, 0.4); }
      50% { box-shadow: 0 0 0 8px rgba(229, 57, 53, 0); }
    }
    .recording-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: white;
      animation: blink 1s step-end infinite;
    }
    @keyframes blink {
      50% { opacity: 0; }
    }
    .recording-indicator-text {
      font-size: 12px;
      font-weight: 500;
      letter-spacing: 0.5px;
    }

    /* Phase 1 multimodal ingest — paperclip + image preview */
    .file-input-hidden {
      display: none;
    }
    .attachment-preview {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 6px;
      margin-bottom: 6px;
      align-self: flex-start;
    }
    .attachment-preview-thumb {
      width: 64px;
      height: 64px;
      object-fit: cover;
      border-radius: 8px;
      flex-shrink: 0;
      border: 1px solid var(--bot-color, #4f46e5);
    }
    .attachment-preview-remove {
      background: transparent;
      border: 1px solid #d1d5db;
      color: #6b7280;
      font-size: 14px;
      line-height: 1;
      cursor: pointer;
      padding: 2px 8px;
      border-radius: 4px;
      flex-shrink: 0;
    }
    .attachment-preview-remove:hover {
      background: rgba(0, 0, 0, 0.06);
      color: #ef4444;
      border-color: #ef4444;
    }
    .paperclip-button {
      background: transparent;
      color: var(--bot-color, #4f46e5);
      border: 1px solid var(--bot-color, #4f46e5);
      border-radius: 50%;
      width: 36px;
      height: 36px;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      transition: background-color 0.2s ease, color 0.2s ease;
      flex-shrink: 0;
      padding: 0;
    }
    .paperclip-button:hover:not(:disabled) {
      background: var(--bot-color, #4f46e5);
      color: white;
    }
    .paperclip-button:disabled {
      color: #9ca3af;
      border-color: #9ca3af;
      cursor: not-allowed;
    }
    .msg-image {
      display: block;
      max-width: 240px;
      max-height: 180px;
      object-fit: cover;
      border-radius: 8px;
      margin: 0;
      cursor: pointer;
    }
    .msg-audio {
      display: block;
      max-width: 240px;
      width: 100%;
      height: 36px;
    }
    .msg-audio::-webkit-media-controls-panel {
      background-color: rgba(255, 255, 255, 0.85);
    }

    /* Phase 1 lightbox modal */
    .lightbox-backdrop {
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.85);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 9999;
      cursor: pointer;
    }
    .lightbox-frame {
      position: relative;
      max-width: 90vw;
      max-height: 90vh;
      cursor: default;
    }
    .lightbox-image {
      display: block;
      max-width: 90vw;
      max-height: 90vh;
      object-fit: contain;
      border-radius: 4px;
    }
    .lightbox-close {
      position: absolute;
      top: -36px;
      right: 0;
      background: transparent;
      border: none;
      color: white;
      font-size: 28px;
      cursor: pointer;
      line-height: 1;
      padding: 4px 8px;
    }
    .lightbox-close:hover {
      opacity: 0.8;
    }

    .send-icon {
      width: 16px;
      height: 16px;
      fill: currentColor;
    }

    .welcome-message {
      text-align: center;
      color: #666;
      font-style: italic;
      margin: 20px 0;
    }

    .typing-indicator {
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 12px 16px;
      background: #f1f3f4;
      border-radius: 18px;
      border-bottom-left-radius: 4px;
      align-self: flex-start;
      max-width: 80%;
    }

    .thinking-message {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 12px 16px;
      background: linear-gradient(90deg,
        rgba(128, 128, 128, 0.1) 0%,
        rgba(128, 128, 128, 0.3) 50%,
        rgba(128, 128, 128, 0.1) 100%);
      background-size: 200% 100%;
      animation: shimmer-slide 1.5s ease-in-out infinite;
      border-radius: 18px;
      border-bottom-left-radius: 4px;
      align-self: flex-start;
      max-width: 80%;
      font-size: 14px;
      color: #666;
    }

    .thinking-message span {
      animation: text-pulse 1.5s ease-in-out infinite;
    }

    @keyframes shimmer-slide {
      0% { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }

    @keyframes text-pulse {
      0%, 100% { opacity: 0.6; }
      50% { opacity: 1; }
    }

    .typing-dot {
      width: 8px;
      height: 8px;
      background: #666;
      border-radius: 50%;
      animation: typing 1.4s infinite ease-in-out;
    }

    .typing-dot:nth-child(1) {
      animation-delay: -0.32s;
    }

    .typing-dot:nth-child(2) {
      animation-delay: -0.16s;
    }

    @keyframes typing {
      0%,
      80%,
      100% {
        transform: scale(0.8);
        opacity: 0.5;
      }
      40% {
        transform: scale(1);
        opacity: 1;
      }
    }

    .error {
      color: #c00;
    }

    .chat-icon {
      width: 24px;
      height: 24px;
      fill: currentColor;
    }

    .close-icon {
      width: 16px;
      height: 16px;
      fill: currentColor;
    }

    .send-icon {
      width: 16px;
      height: 16px;
      fill: currentColor;
    }

    .overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background: transparent;
      z-index: 9998;
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.3s ease;
    }

    .overlay.active {
      opacity: 1;
      pointer-events: all;
    }

    .welcome-message {
      text-align: center;
      color: #666;
      font-style: italic;
      margin: 20px 0;
    }

    .inat-credit {
      text-align: center;
      font-size: 11px;
      color: #aaa;
      margin: 0 16px 2px 0;
    }
    .inat-credit a {
      color: #aaa;
      text-decoration: underline;
      font-size: 11px;
    }

    .feedback-buttons {
      position: absolute;
      bottom: 4px;
      right: 8px;
      display: flex;
      gap: 3px;
    }
    .feedback-btn {
      background: none;
      border: none;
      border-radius: 3px;
      padding: 1px 4px;
      cursor: pointer;
      font-size: 11px;
      line-height: 1;
      opacity: 0.4;
      transition: opacity 0.2s;
    }
    .feedback-btn:hover {
      opacity: 1;
      background: #e0e0e0;
    }
    .feedback-btn.active {
      opacity: 1;
      background: #e3f2fd;
    }
    .feedback-submitted {
      position: absolute;
      bottom: 4px;
      right: 8px;
      font-size: 11px;
      line-height: 1;
    }

    .streaming-bubble::after {
      content: "▋";
      animation: blink 1s step-end infinite;
    }

    @keyframes blink {
      0%,
      100% {
        opacity: 1;
      }
      50% {
        opacity: 0;
      }
    }

    .resize-handle {
      position: absolute;
      z-index: 10002;
      opacity: 0;
      transition: opacity 0.2s;
    }

    .resize-handle:hover {
      opacity: 1;
    }

    .resize-handle--top {
      top: 0;
      left: 8px;
      right: 8px;
      height: 6px;
      cursor: ns-resize;
      background: linear-gradient(to bottom, rgba(0,0,0,0.12), transparent);
      border-radius: 3px 3px 0 0;
    }

    .resize-handle--left {
      left: 0;
      top: 8px;
      bottom: 8px;
      width: 6px;
      cursor: ew-resize;
      background: linear-gradient(to right, rgba(0,0,0,0.12), transparent);
      border-radius: 3px 0 0 3px;
    }

    .resize-handle--corner {
      top: 0;
      left: 0;
      width: 14px;
      height: 14px;
      cursor: nwse-resize;
      border-top: 2px solid rgba(0,0,0,0.2);
      border-left: 2px solid rgba(0,0,0,0.2);
      border-top-left-radius: 6px;
      opacity: 0.4;
    }

    .resize-handle--corner:hover {
      opacity: 0.8;
    }

    @media (max-width: 480px) {
      :host {
        bottom: 16px;
        right: 16px;
      }

      .floating-button {
        width: 56px;
        height: 56px;
        font-size: 22px;
      }

      .header-actions {
        flex-direction: row;
        gap: 4px;
      }

      .new-conversation-button {
        width: 28px;
        height: 28px;
      }

      .new-conversation-icon {
        width: 16px;
        height: 16px;
      }
    }

    /* Human-handoff-mvp: handoff banner. Rendered above the message
       list when handoffActive is true. Pulses softly to signal liveness. */
    .handoff-banner {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px 14px;
      background: linear-gradient(135deg, #1f7aec 0%, #4ea3ff 100%);
      color: #ffffff;
      font-size: 14px;
      font-weight: 500;
      line-height: 1.2;
      border-bottom: 1px solid rgba(0, 0, 0, 0.06);
      animation: handoff-banner-in 220ms ease-out;
      will-change: opacity, transform;
    }

    .handoff-banner-text {
      flex: 1;
    }

    /* Pulsing dot — communicates "live connection" without being noisy. */
    .handoff-banner-dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: #ffffff;
      box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.7);
      animation: handoff-pulse 1.6s ease-out infinite;
      flex-shrink: 0;
    }

    @keyframes handoff-banner-in {
      from {
        opacity: 0;
        transform: translateY(-8px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @keyframes handoff-pulse {
      0% {
        box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.7);
      }
      70% {
        box-shadow: 0 0 0 8px rgba(255, 255, 255, 0);
      }
      100% {
        box-shadow: 0 0 0 0 rgba(255, 255, 255, 0);
      }
    }

    /* Reduced motion: kill the pulse and use a solid dot instead. */
    @media (prefers-reduced-motion: reduce) {
      .handoff-banner {
        animation: none;
      }
      .handoff-banner-dot {
        animation: none;
      }
    }
  `;

  updated(changed: Map<string, any>) {
    // Trigger fetch when apiKeyAttr or integrationId changes (T-010)
    if (changed.has("apiKeyAttr") || changed.has("integrationId")) {
      this.fetchBotData();
      const keyId = this.apiKeyAttr || this.integrationId;
      if (keyId && !this.initialized) {
        this.initSession();
        this.initializeConversation();
      }
      this._loadSavedSize();
    }
    if (changed.has("conversationId") && this.conversationId) {
      this.loadConversationHistory();
    }
    if (changed.has("botData") && this.botData) {
      this.updateBotColors();
    }
  }

  async fetchBotData() {
    // Priority logic: api-key takes precedence over integration-id (T-010)
    // If apiKeyAttr is provided, use it directly for config fetch
    // If not, fallback to integrationId path (backwards compatibility)
    if (!this.apiKeyAttr && !this.integrationId) {
      console.error("ChatbotWidget: No api-key or integration-id provided");
      this.error = "No api-key or integration-id provided";
      return;
    }

    this.loading = true;
    this.error = "";
    this.botData = null;

    console.log("ChatbotWidget: Fetching bot data with:", {
      apiKeyAttr: this.apiKeyAttr ? `${this.apiKeyAttr.slice(0, 12)}...` : null,
      integrationId: this.integrationId
    });

    // Detectar automáticamente la URL del API basándose en el script
    if (!this.apiUrl) {
      const scriptTag = document.querySelector('script[src*="chatbot-widget"]');
      if (scriptTag) {
        const scriptSrc = scriptTag.getAttribute('src') || '';
        const url = new URL(scriptSrc, window.location.href);
        this.apiUrl = `${url.protocol}//${url.host}`;
        console.log("ChatbotWidget: Auto-detected API URL:", this.apiUrl);
      } else {
        // Fallback: usar el mismo origen que la página
        this.apiUrl = window.location.origin;
        console.log("ChatbotWidget: Using page origin as API URL:", this.apiUrl);
      }
    }

    // Determine config URL based on priority (T-011)
    // api-key -> /widget/by-key/{api_key}/config (new path)
    // integration-id -> /widget/{integration_id}/config (legacy path)
    let configUrl: string;
    if (this.apiKeyAttr) {
      configUrl = `${this.apiUrl}/api/v1/widget/by-key/${this.apiKeyAttr}/config`;
      console.log("ChatbotWidget: Using api-key path (new)");
    } else {
      configUrl = `${this.apiUrl}/api/v1/widget/${this.integrationId}/config`;
      console.log("ChatbotWidget: Using integration-id path (legacy fallback)");
    }
    console.log("ChatbotWidget: API URL:", configUrl);

    try {
      const res = await fetch(configUrl);

      console.log("ChatbotWidget: Response status:", res.status);

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }

      const data = await res.json();
      console.log("ChatbotWidget: Response data:", data);

      // Guardar apiKey y apiUrl de la respuesta
      this.apiKey = data.api_key || '';

      // If response includes integration_id, use it for storage (backwards compat)
      if (data.integration_id && !this.integrationId) {
        this.integrationId = data.integration_id;
      }

      // Adaptar el formato de respuesta del backend FastAPI
      this.botData = {
        name: data.bot_name,
        avatar_url: data.avatar_url,
        welcome_message: data.welcome_message,
        chat_color: data.theme?.primary_color || data.primary_color || '#4287f5',
        features: data.features || {},
      };
      console.log("ChatbotWidget: Bot data set:", this.botData);

      this.updateBotColors();

    } catch (e: any) {
      console.error("ChatbotWidget: Error fetching bot data:", e);
      this.error = e.message || "Error de conexion";
    } finally {
      this.loading = false;
      console.log("ChatbotWidget: Loading finished, botData:", this.botData);
    }
  }

  async loadConversationHistory() {
    if (!this.sessionId || !this.integrationId || !this.apiKey) return;

    this.loadingConversationHistory = true;
    try {
      const historyUrl = `${this.apiUrl}/api/v1/widget/${this.integrationId}/history/${this.sessionId}`;
      console.log(
        "ChatbotWidget: Loading conversation history for session:",
        this.sessionId
      );
      const response = await fetch(historyUrl, {
        headers: { 'X-API-Key': this.apiKey },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.messages && Array.isArray(data.messages)) {
          this.messages = data.messages.map((msg: any) => ({
            type: msg.role === "user" ? "user" : "bot",
            content: msg.content,
            timestamp: new Date(msg.timestamp),
            id: msg.id || undefined,
            feedback: msg.metadata?.feedback || undefined,
            feedbackSubmitted: !!msg.metadata?.feedback,
          }));
          console.log(
            "ChatbotWidget: Loaded conversation history, messages count:",
            this.messages.length
          );
          this.scrollToBottom();
        }
      } else {
        console.log(
          "ChatbotWidget: Failed to load conversation history, status:",
          response.status
        );
      }
    } catch (error) {
      console.error(
        "ChatbotWidget: Error loading conversation history:",
        error
      );
    } finally {
      this.loadingConversationHistory = false;
    }
  }

  addBotMessage(content: string, messageId?: number) {
    this.messages = [
      ...this.messages,
      {
        type: "bot",
        content,
        timestamp: new Date(),
        id: messageId,
      },
    ];
    this.scrollToLatestElement();
  }

  addUserMessage(content: string) {
    this.messages = [
      ...this.messages,
      {
        type: "user",
        content,
        timestamp: new Date(),
      },
    ];
    this.scrollToLatestElement();
  }

  /**
   * Human-handoff-mvp: PUBLIC API. Append an agent message to the chat
   * surface. Public (no underscore) per design §8.2 — the widget package
   * exposes this so integrators can drive the widget from their own code
   * without depending on the SSE pipeline. The internal helper is private
   * (underscore-prefixed) so the public method is the only script-stable
   * contract.
   */
  public addAgentMessage(message: AgentMessage): void {
    this._addAgentMessageHelper(message);
    this.requestUpdate();
  }

  /**
   * Internal implementation of {@link addAgentMessage}. Underscore-prefixed
   * by design convention — private, not part of the public API.
   */
  private _addAgentMessageHelper(message: AgentMessage): void {
    this.messages = [
      ...this.messages,
      {
        type: "agent",
        content: message.content,
        timestamp: new Date(),
        agent_id: message.agent_id,
        id: message.id,
      },
    ];
    this.scrollToLatestElement();
  }

  async startNewConversation() {
    this.messages = [];
    this.currentConversationId = null;
    this.conversationTitle = null;
    this.thinkingMessage = "";
    this.streamingContent = "";
    this.isStreaming = false;
    // Human-handoff-mvp: a new conversation abandons any current handoff.
    this.handoffActive = false;
    this.clearConversationId(); // Clear saved conversation ID
  }

  async closeConversation() {
    if (!this.currentConversationId || !this.integrationId) return;

    try {
      // En FastAPI no hay endpoint de cierre, simplemente limpiamos el estado local
      this.currentConversationId = null;
      this.conversationTitle = null;
      this.clearConversationId();
      console.log("ChatbotWidget: Conversation closed locally");
    } catch (error) {
      console.error("ChatbotWidget: Error closing conversation:", error);
    }
  }

  private _isMobile(): boolean {
    return window.innerWidth <= 480;
  }

  private _clampSize(w: number, h: number): { w: number; h: number } {
    const maxW = Math.min(600, window.innerWidth - 40);
    const maxH = Math.min(700, window.innerHeight - 140);
    return {
      w: Math.max(280, Math.min(w, maxW)),
      h: Math.max(350, Math.min(h, maxH)),
    };
  }

  private _loadSavedSize(): void {
    try {
      const saved = localStorage.getItem(`chatbot_size_${this.integrationId}`);
      if (saved) {
        const { w, h } = JSON.parse(saved);
        const clamped = this._clampSize(w, h);
        this._containerWidth = clamped.w;
        this._containerHeight = clamped.h;
      }
    } catch {
      // ignore
    }
  }

  private _saveSize(): void {
    try {
      localStorage.setItem(
        `chatbot_size_${this.integrationId}`,
        JSON.stringify({ w: this._containerWidth, h: this._containerHeight })
      );
    } catch {
      // ignore
    }
  }

  private _onResizeStart(e: MouseEvent, handle: ResizeState['handle']): void {
    if (this._isMobile()) return;
    e.preventDefault();
    this._resizeState = {
      handle,
      startX: e.clientX,
      startY: e.clientY,
      startWidth: this._containerWidth,
      startHeight: this._containerHeight,
    };
  }

  private _onResizeMove = (e: MouseEvent): void => {
    if (!this._resizeState) return;
    if (this._rafId !== null) return;
    this._rafId = requestAnimationFrame(() => {
      this._rafId = null;
      if (!this._resizeState) return;
      const { handle, startX, startY, startWidth, startHeight } = this._resizeState;
      let newW = startWidth;
      let newH = startHeight;
      if (handle === 'top' || handle === 'corner') {
        newH = startHeight - (e.clientY - startY);
      }
      if (handle === 'left' || handle === 'corner') {
        newW = startWidth - (e.clientX - startX);
      }
      const clamped = this._clampSize(newW, newH);
      this._containerWidth = clamped.w;
      this._containerHeight = clamped.h;
    });
  };

  private _onResizeEnd = (): void => {
    if (!this._resizeState) return;
    this._resizeState = null;
    if (this._rafId !== null) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
    this._saveSize();
  };

  private _attachMessageId(messageId: number): void {
    // Find the last bot message without an ID (the one currently streaming)
    const idx = this.messages.findLastIndex(
      (m) => m.type === "bot" && !m.id
    );
    if (idx !== -1) {
      const updated = [...this.messages];
      updated[idx] = { ...updated[idx], id: messageId };
      this.messages = updated;
    }
  }

  private async _submitFeedback(messageId: number, feedback: "up" | "down"): Promise<void> {
    if (!this.integrationId) return;

    // Optimistic UI update
    const idx = this.messages.findIndex((m) => m.id === messageId);
    if (idx === -1) return;
    const updated = [...this.messages];
    updated[idx] = { ...updated[idx], feedback, feedbackSubmitted: true };
    this.messages = updated;

    try {
      const resp = await fetch(
        `${this.apiUrl}/api/v1/widget/${this.integrationId}/message/${messageId}/feedback`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-API-Key": this.apiKey,
          },
          body: JSON.stringify({ feedback }),
        }
      );
      if (!resp.ok) {
        // Revert on failure
        updated[idx] = {
          ...updated[idx],
          feedback: undefined,
          feedbackSubmitted: false,
        };
        this.messages = updated;
      }
    } catch (e) {
      console.error("Feedback submit failed:", e);
      const updated = [...this.messages];
      updated[idx] = {
        ...updated[idx],
        feedback: undefined,
        feedbackSubmitted: false,
      };
      this.messages = updated;
    }
  }

  private _renderFeedbackButtons(msg: {
    type: "user" | "bot";
    content: string;
    timestamp: Date;
    id?: number;
    feedback?: "up" | "down";
    feedbackSubmitted?: boolean;
  }) {
    if (msg.type !== "bot" || !msg.id) return html``;
    if (msg.feedbackSubmitted) {
      const icon = msg.feedback === "up" ? "\uD83D\uDC4D" : "\uD83D\uDC4E";
      return html`<span class="feedback-submitted">${icon}</span>`;
    }
    return html`
      <span class="feedback-buttons">
        <button
          class="feedback-btn ${msg.feedback === "up" ? "active" : ""}"
          @click=${() => this._submitFeedback(msg.id!, "up")}
          title="Helpful"
        >\uD83D\uDC4D</button>
        <button
          class="feedback-btn ${msg.feedback === "down" ? "active" : ""}"
          @click=${() => this._submitFeedback(msg.id!, "down")}
          title="Not helpful"
        >\uD83D\uDC4E</button>
      </span>
    `;
  }

  private get voiceInputEnabled(): boolean {
    return (this.botData as any)?.features?.voice_input ?? false;
  }

  /**
   * Phase 1 multimodal ingest: paperclip is shown when the integration has
   * file_upload_enabled = true (default OFF). When false, the upload button
   * is not rendered — drag-drop is NOT supported in Phase 1 (deferred per
   * proposal §Out-of-Scope and spec §5).
   */
  private get fileUploadEnabled(): boolean {
    return (this.botData as any)?.features?.file_upload ?? false;
  }

  /**
   * Open the lightbox with the given image URL. Backdrop click closes.
   * The lightbox is rendered above the chat surface as a fullscreen modal.
   */
  private openLightbox(url: string): void {
    this.lightboxUrl = url;
  }

  private closeLightbox(): void {
    this.lightboxUrl = null;
  }

  async startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this._audioStream = stream;

      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm";

      this._mediaRecorder = new MediaRecorder(stream, { mimeType });
      this._audioChunks = [];

      this._mediaRecorder.ondataavailable = (e: BlobEvent) => {
        if (e.data.size > 0) this._audioChunks.push(e.data);
      };

      this._mediaRecorder.onstop = () => {
        if (this._audioStream) {
          this._audioStream.getTracks().forEach(track => track.stop());
          this._audioStream = null;
        }

        if (this._audioChunks.length === 0) return;

        const blob = new Blob(this._audioChunks, { type: mimeType });
        this.sendAudioMessage(blob);
      };

      this._mediaRecorder.start();
      this.recordingState = "recording";
      this.recordingDuration = 0;

      this._recordingTimer = window.setInterval(() => {
        this.recordingDuration++;
        this.requestUpdate();
      }, 1000);

    } catch (err) {
      console.error("Error accessing microphone:", err);
      this.recordingState = "idle";
    }
  }

  stopRecording() {
    if (this._mediaRecorder && this._mediaRecorder.state === "recording") {
      this._mediaRecorder.stop();
      this.recordingState = "sending";
      if (this._recordingTimer) {
        clearInterval(this._recordingTimer);
        this._recordingTimer = null;
      }
    }
  }

  async sendAudioMessage(blob: Blob) {
    const audioUrl = URL.createObjectURL(blob);
    const duration = this.recordingDuration;
    this.addUserAudioMessage("", audioUrl, duration);

    this.sendingMessage = true;
    this.streamingContent = "";
    this.thinkingMessage = "";
    this.scrollToLatestElement();

    this._streamController = new AbortController();

    const url = `${this.apiUrl}/api/v1/widget/${this.integrationId}/message/stream/audio`;

    const formData = new FormData();
    formData.append("file", blob, "audio.webm");
    formData.append("session_id", this.sessionId);
    if (this.currentConversationId) {
      formData.append("conversation_id", this.currentConversationId);
    }

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "X-API-Key": this.apiKey,
        },
        body: formData,
        signal: this._streamController.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const jsonStr = line.slice(6).trim();
          if (!jsonStr || jsonStr === "[DONE]") continue;

          let event: any;
          try {
            event = JSON.parse(jsonStr);
          } catch {
            continue;
          }

          if (event.type === "conversation_id") {
            this.saveConversationId(String(event.conversation_id));
            this.currentConversationId = String(event.conversation_id);
          } else if (event.type === "transcription") {
            if (this.messages.length > 0) {
              const lastMsg = this.messages[this.messages.length - 1];
              if (lastMsg.type === "user") {
                const updated = [...this.messages];
                updated[updated.length - 1] = { ...lastMsg, content: event.data || lastMsg.content };
                this.messages = updated;
              }
            }
          } else if (event.type === "thinking") {
            this.thinkingMessage = event.message || "";
          } else if (event.type === "chunk") {
            this.thinkingMessage = "";
            if (!this.isStreaming) {
              this.sendingMessage = false;
              this.isStreaming = true;
            }
            this.streamingContent += event.content;
            this.scrollToLatestElement();
          } else if (event.type === "message") {
            if (event.id) {
              this._attachMessageId(event.id);
            }
          } else if (event.type === "done") {
            this.thinkingMessage = "";
            this.addBotMessage(this.streamingContent, event.message_id);
            this.streamingContent = "";
            this.isStreaming = false;
          } else if (event.type === "error") {
            this.thinkingMessage = "";
            this.addBotMessage(event.message || "Error al procesar el audio.");
            this.streamingContent = "";
            this.isStreaming = false;
          } else if (event.type === "handoff_active") {
            // Human-handoff-mvp: server says a human agent is now (or
            // soon) attached. Show the banner. The event is additive —
            // unknown event types are ignored per spec G3.
            this.handoffActive = true;
          } else if (event.type === "agent_message") {
            // Human-handoff-mvp: an admin agent message arrived.
            // Route through the PUBLIC method (no underscore) so external
            // integrators can call the same code path.
            this.addAgentMessage({
              content: event.content || "",
              agent_id: event.agent_id,
              id: event.message_id,
            });
          } else if (event.type === "handoff_closed") {
            // Human-handoff-mvp: server says the handoff ended. Hide the
            // banner — the next LLM chunk will resume normal flow.
            this.handoffActive = false;
          }
          // Unknown event types fall through silently (spec G3).
        }
      }
    } catch (error: any) {
      if (error?.name === "AbortError") return;
      console.error("Error en audio streaming:", error);
      this.addBotMessage("Error de conexión. Intenta de nuevo.");
      this.streamingContent = "";
      this.thinkingMessage = "";
    } finally {
      this.sendingMessage = false;
      this.isStreaming = false;
      this.thinkingMessage = "";
      this.recordingState = "idle";
    }
  }

  /**
   * Phase 1 multimodal ingest: send an image uploaded via the paperclip button.
   *
   * Mirrors sendAudioMessage SSE parsing but routes to
   * /api/v1/widget/{id}/message/stream/image. Captures a local blob URL
   * (URL.createObjectURL) to render the image in the user bubble immediately.
   *
   * The server may emit an optional `description` event with the vision-model
   * description text (omitted on vision failure per Decision 7b). The user
   * bubble always shows the image (the widget already has the local blob) —
   * only the description text is conditional.
   *
   * SSE event sequence:
   *   - processing → "Analyzing image..."
   *   - description → "<vision description>" (optional; omitted on failure)
   *   - chunk* → incremental LLM tokens
   *   - message → { id }
   *   - done → final
   */
  /**
   * Phase 1 multimodal ingest — drag-and-drop:
   *
   * Accept image files dropped anywhere over the chat body and route them
   * through the same staging flow as the paperclip picker. Only JPEG/PNG are
   * accepted (mirroring the picker accept=). Browser-level e.dataTransfer
   * filtering ensures we don't try to stage non-images.
   *
   * Drag UX uses dragenter/leave depth tracking because leaving+re-entering
   * child elements fires leave+enter pairs; we only flip the overlay when
   * the entire drag leaves the widget.
   */
  private _onChatDragEnter(e: DragEvent): void {
    if (!this.fileUploadEnabled || this.isStreaming || this.sendingMessage) {
      return;
    }
    if (!e.dataTransfer?.types.includes("Files")) return;
    e.preventDefault();
    this._dragDepth += 1;
    this.isDraggingFile = true;
  }

  private _onChatDragOver(e: DragEvent): void {
    if (!this.isDraggingFile) return;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
  }

  private _onChatDragLeave(e: DragEvent): void {
    if (!this.isDraggingFile) return;
    e.preventDefault();
    this._dragDepth = Math.max(0, this._dragDepth - 1);
    if (this._dragDepth === 0) {
      this.isDraggingFile = false;
    }
  }

  private _onChatDrop(e: DragEvent): void {
    if (!this.isDraggingFile) return;
    e.preventDefault();
    this._dragDepth = 0;
    this.isDraggingFile = false;

    const files = e.dataTransfer?.files;
    if (!files || files.length === 0) return;
    const file = files[0];
    if (!file.type || !["image/jpeg", "image/png"].includes(file.type)) {
      console.warn("ChatbotWidget: dropped file rejected (unsupported MIME):", file.type);
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      console.warn("ChatbotWidget: dropped file rejected (>10MB):", file.size);
      this.addBotMessage("La imagen es demasiado grande. Máximo 10 MB.");
      return;
    }
    this.stagePendingAttachment(file);
  }

  /**
   * Phase 1 multimodal ingest — staging flow:
   *
   * When the user picks a file via the paperclip button, we DON'T send it
   * immediately. Instead we store it as `pendingAttachment` and render a
   * thumbnail in the input area. The user can then type a question and click
   * send — both the image AND the question are sent together (Route A+:
   * describe-via-vision produces a description, the description is prefixed,
   * and the user's question follows it). The LLM gets full context
   * (image + question) and answers the actual question.
   *
   * sendMessage() is the single point of dispatch — when pendingAttachment
   * is set, it routes through sendImageMessageWithText() instead of the
   * text-only stream endpoint.
   */
  private stagePendingAttachment(file: File): void {
    if (this.pendingAttachment) {
      URL.revokeObjectURL(this.pendingAttachment.previewUrl);
    }
    const previewUrl = URL.createObjectURL(file);
    this.pendingAttachment = { file, previewUrl };
    if (!this.currentMessage.trim()) {
      this.currentMessage = "¿Qué contiene esta imagen?";
    }
    setTimeout(() => {
      const ta = this.renderRoot.querySelector(
        ".message-input",
      ) as HTMLTextAreaElement | null;
      ta?.focus();
    }, 50);
  }

  private clearPendingAttachment(): void {
    if (this.pendingAttachment) {
      URL.revokeObjectURL(this.pendingAttachment.previewUrl);
      this.pendingAttachment = null;
    }
  }

  async sendImageMessage(file: File, userText?: string) {
    const imageUrl = URL.createObjectURL(file);
    const bubbleContent = userText?.trim() ? userText.trim() : "";
    this.addUserImageMessage(bubbleContent, imageUrl);

    this.sendingMessage = true;
    this.streamingContent = "";
    this.thinkingMessage = "";
    this.scrollToLatestElement();

    this._streamController = new AbortController();

    const url = `${this.apiUrl}/api/v1/widget/${this.integrationId}/message/stream/image`;

    const formData = new FormData();
    formData.append("file", file, file.name || "image.jpg");
    formData.append("session_id", this.sessionId);
    if (this.currentConversationId) {
      formData.append("conversation_id", this.currentConversationId);
    }
    if (userText?.trim()) {
      formData.append("user_message", userText.trim());
    }

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "X-API-Key": this.apiKey,
        },
        body: formData,
        signal: this._streamController.signal,
      });

      // 415/413 from spec REQ-IMG-014 — surface as a bot error message,
      // do NOT throw (the user already sees the image in the bubble).
      if (!response.ok) {
        let errMsg = `HTTP ${response.status}`;
        try {
          const errBody = await response.json();
          if (errBody?.detail) errMsg = errBody.detail;
        } catch {
          // ignore parse errors
        }
        throw new Error(errMsg);
      }

      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let lastDescription = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const jsonStr = line.slice(6).trim();
          if (!jsonStr || jsonStr === "[DONE]") continue;

          let event: any;
          try {
            event = JSON.parse(jsonStr);
          } catch {
            continue;
          }

          if (event.type === "conversation_id") {
            this.saveConversationId(String(event.conversation_id));
            this.currentConversationId = String(event.conversation_id);
          } else if (event.type === "processing") {
            this.thinkingMessage = event.data || "Analyzing image...";
          } else if (event.type === "description") {
            // Server emitted a vision description — keep for potential
            // debugging; the image is already in the user bubble.
            lastDescription = event.data || "";
            this.thinkingMessage = "";
          } else if (event.type === "thinking") {
            this.thinkingMessage = event.message || "";
          } else if (event.type === "chunk") {
            this.thinkingMessage = "";
            if (!this.isStreaming) {
              this.sendingMessage = false;
              this.isStreaming = true;
            }
            this.streamingContent += event.content;
            this.scrollToLatestElement();
          } else if (event.type === "message") {
            if (event.id) {
              this._attachMessageId(event.id);
            }
          } else if (event.type === "done") {
            this.thinkingMessage = "";
            this.addBotMessage(this.streamingContent, event.message_id);
            this.streamingContent = "";
            this.isStreaming = false;
          } else if (event.type === "error") {
            this.thinkingMessage = "";
            this.addBotMessage(event.message || "Error al procesar la imagen.");
            this.streamingContent = "";
            this.isStreaming = false;
          } else if (event.type === "handoff_active") {
            // Human-handoff-mvp: server says a human agent is now (or
            // soon) attached. Show the banner. The event is additive —
            // unknown event types are ignored per spec G3.
            this.handoffActive = true;
          } else if (event.type === "agent_message") {
            // Human-handoff-mvp: an admin agent message arrived.
            // Route through the PUBLIC method (no underscore) so external
            // integrators can call the same code path.
            this.addAgentMessage({
              content: event.content || "",
              agent_id: event.agent_id,
              id: event.message_id,
            });
          } else if (event.type === "handoff_closed") {
            // Human-handoff-mvp: server says the handoff ended. Hide the
            // banner — the next LLM chunk will resume normal flow.
            this.handoffActive = false;
          }
          // Unknown event types fall through silently (spec G3).
        }
      }
    } catch (error: any) {
      if (error?.name === "AbortError") return;
      console.error("Error en image streaming:", error);
      this.addBotMessage(
        error?.message || "Error de conexión. Intenta de nuevo."
      );
      this.streamingContent = "";
      this.thinkingMessage = "";
    } finally {
      this.sendingMessage = false;
      this.isStreaming = false;
      this.thinkingMessage = "";
    }
  }

  /**
   * Push a user message bubble that includes an image preview (thumbnail).
   * Used by sendImageMessage so the user sees their image immediately while
   * the vision model is processing.
   */
  private addUserImageMessage(content: string, imageUrl: string): void {
    this.messages = [
      ...this.messages,
      {
        type: "user",
        content,
        timestamp: new Date(),
        image_url: imageUrl,
      },
    ];
    this.scrollToLatestElement();
  }

  private addUserAudioMessage(content: string, audioUrl: string, duration: number): void {
    this.messages = [
      ...this.messages,
      {
        type: "user",
        content,
        timestamp: new Date(),
        audio_url: audioUrl,
        audio_duration: duration,
      },
    ];
    this.scrollToLatestElement();
  }

  async sendMessage() {
    const hasText = !!this.currentMessage.trim();
    const hasAttachment = !!this.pendingAttachment;
    if ((!hasText && !hasAttachment) || this.sendingMessage || this.isStreaming) {
      return;
    }

    if (hasAttachment && !hasText) {
      this.currentMessage = "¿Qué contiene esta imagen?";
    }

    const message = this.currentMessage.trim();

    if (hasAttachment) {
      const attachment = this.pendingAttachment!;
      this.clearPendingAttachment();
      this.currentMessage = "";
      this.sendingMessage = true;
      this.streamingContent = "";
      this.thinkingMessage = "";
      this.scrollToLatestElement();
      await this.sendImageMessage(attachment.file, message);
      return;
    }

    this.addUserMessage(message);
    this.currentMessage = "";
    this.sendingMessage = true;
    this.streamingContent = "";
    this.thinkingMessage = "";

    // Scroll to show the typing indicator
    this.scrollToLatestElement();

    // Guard: apiUrl or integrationId missing
    if (!this.apiUrl || !this.integrationId) {
      this.addBotMessage("Error: el widget no está configurado correctamente.");
      this.sendingMessage = false;
      return;
    }

    this._streamController = new AbortController();
    const reader_ref: { reader: ReadableStreamDefaultReader<Uint8Array> | null } = { reader: null };

    try {
      const url = `${this.apiUrl}/api/v1/widget/${this.integrationId}/message/stream`;
      console.log("ChatbotWidget: Sending message (stream) to:", url);

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": this.apiKey,
        },
        body: JSON.stringify({
          message,
          session_id: this.sessionId,
          conversation_id: this.currentConversationId ?? null,
        }),
        signal: this._streamController.signal,
      });

      console.log("ChatbotWidget: Stream response status:", response.status);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const reader = response.body!.getReader();
      reader_ref.reader = reader;
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const jsonStr = line.slice(6).trim();
          if (!jsonStr || jsonStr === "[DONE]") continue;

          let event: any;
          try {
            event = JSON.parse(jsonStr);
          } catch {
            continue;
          }

          if (event.type === "conversation_id") {
            this.saveConversationId(String(event.conversation_id));
            this.currentConversationId = String(event.conversation_id);
          } else if (event.type === "thinking") {
            this.thinkingMessage = event.message || "";
          } else if (event.type === "chunk") {
            this.thinkingMessage = "";
            if (!this.isStreaming) {
              this.sendingMessage = false;
              this.isStreaming = true;
            }
            this.streamingContent += event.content;
            this.scrollToLatestElement();
          } else if (event.type === "message") {
            // Backend saved the assistant message — attach ID to last bot message (backup mechanism)
            if (event.id) {
              this._attachMessageId(event.id);
            }
          } else if (event.type === "done") {
            this.thinkingMessage = "";
            this.addBotMessage(this.streamingContent, event.message_id);
            this.streamingContent = "";
            this.isStreaming = false;
          } else if (event.type === "error") {
            this.thinkingMessage = "";
            this.addBotMessage(event.message || "Error al procesar el mensaje.");
            this.streamingContent = "";
            this.isStreaming = false;
          } else if (event.type === "handoff_active") {
            // Human-handoff-mvp: server says a human agent is now (or
            // soon) attached. Show the banner. The event is additive —
            // unknown event types are ignored per spec G3.
            this.handoffActive = true;
          } else if (event.type === "agent_message") {
            // Human-handoff-mvp: an admin agent message arrived.
            // Route through the PUBLIC method (no underscore) so external
            // integrators can call the same code path.
            this.addAgentMessage({
              content: event.content || "",
              agent_id: event.agent_id,
              id: event.message_id,
            });
          } else if (event.type === "handoff_closed") {
            // Human-handoff-mvp: server says the handoff ended. Hide the
            // banner — the next LLM chunk will resume normal flow.
            this.handoffActive = false;
          }
          // Unknown event types fall through silently (spec G3).
        }
      }
    } catch (error: any) {
      if (error?.name === "AbortError") return;
      console.error("ChatbotWidget: Error en streaming:", error);
      this.addBotMessage("Error de conexión. Intenta de nuevo.");
      this.streamingContent = "";
      this.thinkingMessage = "";
    } finally {
      if (reader_ref.reader) {
        try { reader_ref.reader.releaseLock(); } catch { /* already released */ }
      }
      this.sendingMessage = false;
      this.isStreaming = false;
      this.thinkingMessage = "";
      this._streamController = null;
    }
  }

  handleKeyPress(e: KeyboardEvent) {
    e.stopPropagation();
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      this.sendMessage();
    }
  }

  toggleChat() {
    this.isOpen = !this.isOpen;
  }

  closeChat() {
    this.isOpen = false;
  }

  handleOverlayClick(e: Event) {
    const target = e.target as HTMLElement;
    const host = this.shadowRoot?.host;

    if (target === e.currentTarget && !host?.contains(target)) {
      this.closeChat();
    }
  }

  render() {
    console.log("ChatbotWidget: Rendering with botData:", this.botData);
    console.log("ChatbotWidget: Loading:", this.loading);
    console.log("ChatbotWidget: Error:", this.error);
    console.log(
      "ChatbotWidget: Current conversation ID:",
      this.currentConversationId
    );

    return html`
      <div
        class="overlay ${this.isOpen ? "active" : ""}"
        @click=${this.handleOverlayClick}
      ></div>

      ${this.botData
        ? html`
            <button
              class="floating-button"
              @click=${(e: Event) => {
                e.stopPropagation();
                this.toggleChat();
              }}
            >
              <svg class="chat-icon" viewBox="0 0 24 24">
                <path
                  d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h4l4 4 4-4h4c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"
                />
              </svg>
            </button>
          `
        : ""}

      <div
        class="resize-wrapper"
        style=${styleMap({
          width: `${this._containerWidth}px`,
          height: `${this._containerHeight}px`,
          pointerEvents: this.isOpen ? 'all' : 'none',
        })}
      >
        <div
          class="chat-container ${this.isOpen ? "open" : ""}"
          @click=${(e: Event) => e.stopPropagation()}
        >
          <div class="chat-header">
            <div class="bot-info">
              <img
                src="${this.botData?.avatar_url ||
                "https://via.placeholder.com/40"}"
                alt="Bot Avatar"
                class="bot-avatar"
              />
              <div class="bot-name">${this.botData?.name || "Chat"}</div>
            </div>
            <div class="header-actions">
              ${this.currentConversationId
                ? html`
                    <button
                      class="new-conversation-button"
                      @click=${(e: Event) => {
                        e.stopPropagation();
                        this.startNewConversation();
                      }}
                      title="Nueva conversación"
                    >
                      <svg class="new-conversation-icon" viewBox="0 0 24 24">
                        <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                      </svg>
                    </button>
                  `
                : ""}
              <button
                class="close-button"
                @click=${(e: Event) => {
                  e.stopPropagation();
                  this.closeChat();
                }}
              >
                <svg class="close-icon" viewBox="0 0 24 24">
                  <path
                    d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
                  />
                </svg>
              </button>
            </div>
          </div>
          <div
            class="chat-body"
            @dragenter=${(e: DragEvent) => this._onChatDragEnter(e)}
            @dragover=${(e: DragEvent) => this._onChatDragOver(e)}
            @dragleave=${(e: DragEvent) => this._onChatDragLeave(e)}
            @drop=${(e: DragEvent) => this._onChatDrop(e)}
          >
            ${this.handoffActive
              ? html`
                  <div class="handoff-banner" role="status" aria-live="polite">
                    <span class="handoff-banner-dot" aria-hidden="true"></span>
                    <span class="handoff-banner-text">
                      Conectado con un agente humano
                    </span>
                  </div>
                `
              : ""}
            ${this.loading
              ? html`<div class="welcome-message">Cargando datos del bot...</div>`
              : this.error
              ? html`<div class="error">Error: ${this.error}</div>`
              : !this.botData
              ? html`
                  <div class="welcome-message">
                    <div>Sin datos del bot</div>
                    <div style="margin-top:8px; font-size:0.9em; color:#aaa;">
                      Integration ID: ${this.integrationId || "No proporcionado"}
                    </div>
                    <div style="margin-top:4px; font-size:0.8em; color:#ccc;">
                      URL: ${this.apiUrl}
                    </div>
                  </div>
                `
              : html`
                  <div class="messages-container">
                    ${this.loadingConversationHistory
                      ? html`<div class="welcome-message">
                          Cargando historial de conversación...
                        </div>`
                      : this.messages.length === 0
                      ? html`<div class="welcome-message">
                          ${this.botData.welcome_message ||
                          "¡Hola! ¿En qué puedo ayudarte?"}
                        </div>`
                      : this.messages.map(
                          (msg) => html`
                            <div class="message ${msg.type}${msg.image_url ? " has-image" : ""}${msg.audio_url ? " has-audio" : ""}">
                              ${msg.image_url
                                ? html`
                                    <img
                                      class="msg-image"
                                      src=${msg.image_url}
                                      alt="Imagen enviada"
                                      @click=${(e: Event) => {
                                        e.stopPropagation();
                                        this.openLightbox(msg.image_url!);
                                      }}
                                    />
                                  `
                                : ""}
                              ${msg.audio_url
                                ? html`
                                    <audio
                                      class="msg-audio"
                                      controls
                                      preload="metadata"
                                      src=${msg.audio_url}
                                    ></audio>
                                  `
                                : ""}
                              ${msg.type === "agent"
                                ? html`
                                    <span class="agent-badge" aria-label="Mensaje de un agente humano">
                                      Agente
                                    </span>
                                  `
                                : ""}
                              ${!msg.audio_url && (msg.type === "bot" || msg.type === "agent")
                                ? unsafeHTML(this.formatMessage(msg.content))
                                : !msg.audio_url
                                ? msg.content
                                : ""}
                              ${msg.type === "bot"
                                ? this._renderFeedbackButtons(msg)
                                : ""}
                            </div>
                          `
                        )}
                    ${this.sendingMessage || this.thinkingMessage
                      ? html`
                          <div class="thinking-message">
                            <span>${this.thinkingMessage || 'Pensando...'}</span>
                          </div>
                        `
                      : ""}
                    ${this.isStreaming
                      ? html`
                          <div class="message bot streaming-bubble">
                            ${unsafeHTML(this.formatMessage(this.streamingContent))}
                          </div>
                        `
                      : ""}
                  </div>
                  <div class="inat-credit">
                    Creado por
                    <a
                      href="https://inteligencianatural.com.ar/"
                      target="_blank"
                      rel="noopener"
                      style="color:inherit;text-decoration:underline;"
                      >INAT</a
                    >
                  </div>
                  <div
                    class="message-input-container"
                    @click=${(e: Event) => e.stopPropagation()}
                  >
                    ${this.pendingAttachment
                      ? html`
                          <div class="attachment-preview">
                            <img
                              class="attachment-preview-thumb"
                              src=${this.pendingAttachment.previewUrl}
                              alt="Vista previa"
                              data-testid="attachment-preview-thumb"
                            />
                            <button
                              class="attachment-preview-remove"
                              @click=${(e: Event) => {
                                e.stopPropagation();
                                this.clearPendingAttachment();
                              }}
                              title="Quitar imagen"
                              aria-label="Quitar imagen"
                            >
                              ×
                              </button>
                            </div>
                          </div>
                        `
                      : ""}
                    <div class="message-input-row">
                    ${this.fileUploadEnabled && !this.isStreaming
                      ? html`
                          <input
                            type="file"
                            accept="image/jpeg,image/png"
                            class="file-input-hidden"
                            @change=${(e: Event) => {
                              const input = e.target as HTMLInputElement;
                              const file = input.files?.[0];
                              // Reset value so the same file can be selected again
                              input.value = "";
                              if (file) {
                                this.stagePendingAttachment(file);
                              }
                            }}
                          />
                          <button
                            class="paperclip-button"
                            @click=${(e: Event) => {
                              e.stopPropagation();
                              const input = this.renderRoot.querySelector(
                                ".file-input-hidden"
                              ) as HTMLInputElement | null;
                              input?.click();
                            }}
                            ?disabled=${this.sendingMessage || this.isStreaming}
                            title="Adjuntar imagen (JPEG o PNG, máx 10 MB)"
                          >
                            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                              <path d="M16.5 6v11.5a4.5 4.5 0 1 1-9 0V5a2.5 2.5 0 0 1 5 0v10.5a1 1 0 1 1-2 0V6H9v9.5a3 3 0 1 0 6 0V5a4.5 4.5 0 0 0-9 0v12.5a6 6 0 1 0 12 0V6h-1.5z"/>
                            </svg>
                          </button>
                        `
                      : ""}
                    ${this.voiceInputEnabled && !this.isStreaming
                      ? html`
                          <button
                            class="mic-button ${this.recordingState === 'recording' ? 'recording' : ''}"
                            @click=${(e: Event) => {
                              e.stopPropagation();
                              if (this.recordingState === 'recording') {
                                this.stopRecording();
                              } else if (this.recordingState === 'idle') {
                                this.startRecording();
                              }
                            }}
                            ?disabled=${this.sendingMessage || this.isStreaming}
                            title=${this.recordingState === 'recording' ? 'Detener grabación' : 'Grabar audio'}
                          >
                            ${this.recordingState === 'recording'
                              ? html`
                                  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                                    <rect x="6" y="4" width="4" height="16" rx="1"/>
                                    <rect x="14" y="4" width="4" height="16" rx="1"/>
                                  </svg>
                                  <span class="recording-dot"></span>
                                  <span class="recording-indicator-text">${String(Math.floor(this.recordingDuration / 60)).padStart(2, '0')}:${String(this.recordingDuration % 60).padStart(2, '0')}</span>
                                `
                              : html`
                                  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                                    <path d="M12 2a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z"/>
                                    <path d="M19 10v2a7 7 0 0 1-14 0v-2H3v2a9 9 0 0 0 8 8.94V22h2v-1.06A9 9 0 0 0 21 12v-2z"/>
                                  </svg>
                                `
                            }
                          </button>
                        `
                      : ""
                    }
                    <textarea
                      class="message-input"
                      placeholder="Escribe tu mensaje..."
                      .value=${this.currentMessage}
                      @input=${(e: Event) => {
                        e.stopPropagation();
                        this.currentMessage = (
                          e.target as HTMLTextAreaElement
                        ).value;
                      }}
                      @keypress=${this.handleKeyPress}
                      @click=${(e: Event) => e.stopPropagation()}
                      ?disabled=${this.sendingMessage || this.isStreaming}
                    ></textarea>
                    <button
                      class="send-button"
                      @click=${(e: Event) => {
                        e.stopPropagation();
                        this.sendMessage();
                      }}
                      ?disabled=${(!this.currentMessage.trim() &&
                        !this.pendingAttachment) ||
                      this.sendingMessage ||
                      this.isStreaming}
                    >
                      <svg class="send-icon" viewBox="0 0 24 24">
                        <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                      </svg>
                    </button>
                    </div>
                  </div>
                `}
            ${this.isDraggingFile
              ? html`
                  <div
                    class="drop-zone-overlay"
                    role="status"
                    aria-live="polite"
                    data-testid="drop-zone-overlay"
                  >
                    <div class="drop-zone-card">
                      <svg
                        viewBox="0 0 24 24"
                        width="40"
                        height="40"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path d="M19.35 10.04A7.49 7.49 0 0 0 12 4C9.11 4 6.6 5.64 5.35 8.04A5.994 5.994 0 0 0 0 14a6 6 0 0 0 6 6h13a5 5 0 0 0 5-5c0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z"/>
                      </svg>
                      <span class="drop-zone-title">Soltá la imagen para adjuntarla</span>
                      <span class="drop-zone-subtitle">JPEG o PNG, máx 10 MB</span>
                    </div>
                  </div>
                `
              : ""}
          </div>
        </div>
        ${this.isOpen && !this._isMobile() ? html`
          <div
            class="resize-handle resize-handle--top"
            @mousedown=${(e: MouseEvent) => this._onResizeStart(e, 'top')}
          ></div>
          <div
            class="resize-handle resize-handle--left"
            @mousedown=${(e: MouseEvent) => this._onResizeStart(e, 'left')}
          ></div>
          <div
            class="resize-handle resize-handle--corner"
            @mousedown=${(e: MouseEvent) => this._onResizeStart(e, 'corner')}
          ></div>
        ` : ''}
      </div>
      ${this.lightboxUrl
        ? html`
            <div
              class="lightbox-backdrop"
              @click=${(e: Event) => {
                // Click on the backdrop (not on the image) closes the lightbox.
                if ((e.target as HTMLElement).classList.contains("lightbox-backdrop")) {
                  this.closeLightbox();
                }
              }}
            >
              <div class="lightbox-frame">
                <button
                  class="lightbox-close"
                  @click=${() => this.closeLightbox()}
                  title="Cerrar"
                >×</button>
                <img
                  class="lightbox-image"
                  src=${this.lightboxUrl}
                  alt="Vista ampliada"
                />
              </div>
            </div>
          `
        : ""}
    `;
  }
}
